import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Building2, Loader2 } from "lucide-react";
import SEO from "@/components/SEO";
import { api } from "@/lib/api";
import { toast } from "sonner";

const inputCls = "w-full rounded-xl bg-black/40 border border-[color:var(--border-subtle)] px-4 py-3 text-sm text-white placeholder:text-white/35 focus:outline-none focus:ring-2 focus:ring-[color:var(--ring)]";

export default function PartnerAuth() {
  const nav = useNavigate();
  const [mode, setMode] = useState("login");
  const [busy, setBusy] = useState(false);
  const [f, setF] = useState({ company: "", contact_name: "", email: "", password: "", phone: "", business_type: "" });
  const upd = (k) => (e) => setF({ ...f, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      if (mode === "register") {
        if (!f.company || !f.contact_name || !f.email || !f.password) { toast.error("Please complete all required fields."); setBusy(false); return; }
        await api.post("/partners/register", f);
        toast.success("Welcome to the Ascendia partner portal");
      } else {
        const { data } = await api.post("/partners/login", { email: f.email, password: f.password });
        toast.success(`Welcome back, ${data.contact_name || ""}`.trim());
      }
      nav("/partners");
    } catch (err) { toast.error(err?.response?.data?.detail || "Something went wrong"); } finally { setBusy(false); }
  };

  return (
    <>
      <SEO title="Partner Portal" description="Ascendia partner portal for contractors, developers, agents, designers and property managers." noindex />
      <div className="min-h-[80vh] flex items-center justify-center px-4 py-16">
        <div className="w-full max-w-md">
          <div className="text-center mb-6">
            <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-white/5 border border-[color:var(--border-subtle)]"><Building2 className="h-6 w-6 text-gold-soft" /></div>
            <h1 className="font-display text-2xl font-semibold text-white">Partner Portal</h1>
            <p className="text-sm text-muted-c mt-1">For contractors, developers, agents, designers & property managers.</p>
          </div>
          <div className="flex rounded-xl border border-[color:var(--border-subtle)] bg-white/[0.03] p-1 mb-5">
            <button onClick={() => setMode("login")} data-testid="partner-tab-login" className={`flex-1 rounded-lg py-2 text-sm font-medium ${mode === "login" ? "bg-gold text-black" : "text-white/70"}`}>Sign in</button>
            <button onClick={() => setMode("register")} data-testid="partner-tab-register" className={`flex-1 rounded-lg py-2 text-sm font-medium ${mode === "register" ? "bg-gold text-black" : "text-white/70"}`}>Register</button>
          </div>
          <form onSubmit={submit} className="ascendia-card p-6 space-y-3">
            {mode === "register" && (<>
              <input className={inputCls} placeholder="Company name*" value={f.company} onChange={upd("company")} data-testid="partner-company" />
              <input className={inputCls} placeholder="Contact name*" value={f.contact_name} onChange={upd("contact_name")} data-testid="partner-contact" />
              <div className="grid grid-cols-2 gap-3"><input className={inputCls} placeholder="Phone" value={f.phone} onChange={upd("phone")} /><input className={inputCls} placeholder="Business type" value={f.business_type} onChange={upd("business_type")} /></div>
            </>)}
            <input className={inputCls} type="email" placeholder="Email*" value={f.email} onChange={upd("email")} data-testid="partner-email" />
            <input className={inputCls} type="password" placeholder="Password*" value={f.password} onChange={upd("password")} data-testid="partner-password" />
            <button type="submit" disabled={busy} data-testid="partner-submit" className="w-full inline-flex items-center justify-center gap-2 rounded-xl bg-gold px-6 py-3 text-sm font-semibold text-black hover:bg-gold-soft disabled:opacity-50">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : (mode === "register" ? "Create partner account" : "Sign in")}</button>
          </form>
          <a href="/" className="mt-6 block text-center text-sm text-muted-c hover:text-white">← Back to site</a>
        </div>
      </div>
    </>
  );
}
